import React, { lazy, Suspense, useEffect, useState } from "react";
import {
    TextField,
    Checkbox,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    MenuItem,
    Typography,
    Switch,
    Tooltip,
    Box,
    Select,
    InputLabel,
    Button,
    IconButton
} from "@mui/material";
import InputMask from "react-input-mask";
import { FieldElement, ControlElement, TableField, TableRelationship, Table } from "../../types/interfaces";
import { useFormContext } from "./FormContext";
import { triggerUserEvent } from "./formEventEngine";
import API from "../../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import { getMimeTypeFromExtension } from "../../utils/layoutUtils"; // if not already imported
import BlobViewerDialog from "../BlobViewerDialog";



interface Props {
    field: FieldElement | ControlElement;
    fieldMeta?: TableField;
    recordData?: Record<string, any>;
    tableFields: TableField[];
}

const LazyRelatedTableGrid = lazy(() => import("./RelatedTableGrid"));

const ControlRenderer: React.FC<Props> = ({ field, fieldMeta, recordData, tableFields }) => {
    const {
        data,
        errors,
        touched,
        setValue,
        visible,
        readonly,
    } = useFormContext();

    const fieldId = (field as any).fieldID;
    const label = (field as any).fieldName ?? fieldMeta?.fieldName ?? fieldId;
    const required = (field as any).required ?? fieldMeta?.required ?? false;
    const readOnly = readonly[fieldId] ?? (field as any).readOnly ?? fieldMeta?.readOnly ?? false;
    const isVisible = visible[fieldId] ?? true;
    const error = touched[fieldId] && errors[fieldId];
    const value = data[fieldId] ?? recordData?.[fieldId] ?? "";
    const navigate = useNavigate();

    const inputMask = fieldMeta?.inputMask || (field as any).inputMask || "";
    const displayMask = (field as any).displayMask ?? fieldMeta?.format ?? undefined;
    const dataType = (field as any).dataType?.toUpperCase() ?? fieldMeta?.dataType?.toUpperCase();
    const fieldType = (field as any).type?.toUpperCase() ?? "TEXT";

    const relationshipID = (field as any)?.relationshipID ?? "";
    const [relationship, setRelationship] = useState<TableRelationship | null>(null);
    const [childTableName, setChildTableName] = useState<string>("");
    const [loadingRel, setLoadingRel] = useState(false);

    const [parentFieldValue, setParentFieldValue] = useState<string>("");
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerData, setViewerData] = useState<{
      fieldLabel: string;
      fileID: string;
      mimeType?: string;
      isBlob: boolean;
    } | null>(null);
    
    useEffect(() => {
        if (!relationship || !relationship.foreignKeyField) return;
    
        // Try to find the actual fieldID that matches the foreignKeyField (which is the fieldName)
        //const matchingField = fieldMeta
        //    ? fieldMeta.fieldID // if fieldMeta matches, shortcut
        //    : Object.entries(recordData || {}).find(([key]) => {
        //        const meta = fieldMeta?.fieldName || "";
        //        return fieldMeta && fieldMeta.fieldName === relationship.foreignKeyField;
        //    });
    
        // Or just brute search if you have tableFields in scope (best option if available)
        // Example if you pass in tableFields:
        const match = tableFields.find(f => f.fieldName === relationship.foreignKeyField);
        let keyFieldID: any = null;
        let val: any = null;

        if(match)
        {
            keyFieldID = match.fieldID;
            
            if (keyFieldID) {
                val = data?.[keyFieldID] ?? recordData?.[keyFieldID];
            }
        
            if (val) {
                setParentFieldValue(val);
            }
        }

           

    }, [relationship, data, recordData]);
    

    useEffect(() => {
        if (!relationshipID) return;
        if(relationshipID==='') return;
        const loadRelationship = async () => {
            try {
                setLoadingRel(true);
                const user = JSON.parse(localStorage.getItem('user') || "{}");
                const headers = {
                    headers: {
                        authToken: user.user?.authtoken,
                        sessionId: user.user?.sessionId,
                    },
                };

                const relRes = await API.get(`/tables/relationship/${relationshipID}`, headers);
                const rel: TableRelationship = relRes.data;
                setRelationship(rel);

                if (rel.childTableID) {
                    const tableRes = await API.get(`/tables/${rel.childTableID}`, headers);
                    const table: Table = tableRes.data;
                    setChildTableName(table.tableName ?? "Related Records");
                } else {
                    setChildTableName("Related Records");
                }
            } catch (err) {
                console.error("[ControlRenderer] Failed to load relationship metadata:", err);
                setChildTableName("Related Records");
            } finally {
                setLoadingRel(false);
            }
        };

        loadRelationship();
    }, [relationshipID]);

    const handleChange = (e: React.ChangeEvent<any>) => {
        const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setValue((field as any).fieldID, val);
        triggerUserEvent(fieldId, "OnChange", { fieldId, value: val });
    };

    const commonProps = {
        id: fieldId,
        name: fieldId,
        label,
        fullWidth: true,
        margin: "dense" as const,
        value: data[fieldId] ?? recordData?.[fieldId] ?? "",
        onBlur: () => triggerUserEvent(fieldId, "OnExit", { fieldId, value }),
        onFocus: () => triggerUserEvent(fieldId, "OnFocus", { fieldId, value }),
        required,
        error: !!error,
        helperText: error,
        InputProps: {
            readOnly : fieldMeta?.readOnly || false,
            required : fieldMeta?.required || false,
            inputMask: fieldMeta?.inputMask,
            maxLength: fieldMeta?.length || undefined,
        },
    };

    if (!isVisible) return null;


    if (fieldType === "TABLE") {
        return (
            <Box mt={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {loadingRel ? "Loading related data..." : childTableName || label}
                </Typography>

                {loadingRel ? (
                    <Typography variant="body2" color="text.secondary">
                        Loading relationship configuration...
                    </Typography>
                ) : !relationshipID ? (
                    <Typography variant="body2">Missing relationship definition.</Typography>
                ) : !relationship ? (
                    <Typography variant="body2" color="text.secondary">
                        Relationship metadata not loaded.
                    </Typography>
                ) : !parentFieldValue ? (
                    <Typography variant="body2" color="text.secondary">
                        No parent value provided.
                    </Typography>
                ) : (
                    <Suspense fallback={<Typography variant="body2">Loading related table...</Typography>}>
                        <LazyRelatedTableGrid
                            key={relationship.childTableID}
                            relationshipId={relationship.relationshipID}
                            parentFieldValue={parentFieldValue}
                        />
                    </Suspense>
                )}
            </Box>
        );
    }

    // (The rest of your field rendering stays IDENTICAL below here -- clean, complete.)

    if (["TEXT", "TEXTBOX", "EMAIL", "DECIMAL", "INTEGER", "NUMBER", "NUMERIC"].includes(dataType)) {
        const isEmail = dataType === "EMAIL";
        const isNumeric = ["NUMBER", "INTEGER", "DECIMAL", "NUMERIC"].includes(dataType);
        const isMasked = inputMask && inputMask.includes("#");

        if (isMasked) {
            return (
                <InputMask
                    mask={inputMask}
                    maskChar="_"
                    value={value}
                    onChange={handleChange}
                    disabled={readOnly}
                >
                    {(inputProps: any) => (
                        <TextField
                            {...commonProps}
                            {...inputProps}
                            type="tel"
                        />
                    )}
                </InputMask>
            );
        }

        return (
            <TextField
                {...commonProps}
                type={isEmail ? "email" : isNumeric ? "tel" : "text"}
                onChange={handleChange}
            />
        );
    }

    switch (dataType) {
        case "BOOLEAN":
        case "CHECKBOX": {
            let options: string[] = [];

            try {
                if (fieldMeta?.listValues) {
                    const parsed = JSON.parse(fieldMeta.listValues);
                    if (Array.isArray(parsed) && parsed.length >= 2) {
                        options = parsed.map(v => String(v));
                    }
                }
            } catch {
                // ignore bad JSON
            }

            const hasCustomLabels = options.length === 2;
            const currentVal = String(value ?? "").toLowerCase(); // "true" / "false" / ""

            const currentBool = currentVal === "true" ? true : currentVal === "false" ? false : null;

            const labelTrue = hasCustomLabels ? options[0] : "Yes";
            const labelFalse = hasCustomLabels ? options[1] : "No";

            return (
                <FormControl fullWidth margin="dense">
                    <Typography variant="subtitle2" gutterBottom>{label}</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Button
                            variant={currentBool === true ? "contained" : "outlined"}
                            onClick={() => {
                                setValue(fieldId, "true");
                                triggerUserEvent(fieldId, "OnChange", { fieldId, value: "true" });
                            }}
                            disabled={readOnly}
                        >
                            {labelTrue}
                        </Button>
                        <Button
                            variant={currentBool === false ? "contained" : "outlined"}
                            onClick={() => {
                                setValue(fieldId, "false");
                                triggerUserEvent(fieldId, "OnChange", { fieldId, value: "false" });
                            }}
                            disabled={readOnly}
                        >
                            {labelFalse}
                        </Button>

                        {currentBool !== null && (
                            <Tooltip title="Clear value">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setValue(fieldId, "");
                                        triggerUserEvent(fieldId, "OnChange", { fieldId, value: "" });
                                    }}
                                    sx={{ color: "error.main", ml: 1 }}
                                >
                                    <span className="material-icons">clear</span>
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </FormControl>
            );
        }

            

        case "SWITCH":
            return (
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!value}
                            onChange={handleChange}
                            onBlur={commonProps.onBlur}
                            disabled={readOnly}
                        />
                    }
                    label={label}
                />
            );

        case "RADIO":
            return (
                <FormControl fullWidth margin="dense">
                    <FormLabel>{label}</FormLabel>
                    <RadioGroup
                        row
                        name={fieldId}
                        value={value}
                        onChange={handleChange}
                        onBlur={commonProps.onBlur}
                    >
                        {(fieldMeta?.listValues ?? "")
                            .split(",")
                            .map((opt) => (
                                <FormControlLabel
                                    key={opt.trim()}
                                    value={opt.trim()}
                                    control={<Radio />}
                                    label={opt.trim()}
                                    disabled={readOnly}
                                />
                            ))}
                    </RadioGroup>
                </FormControl>
            );

        case "LISTBOX":
        case "DROPDOWN":
        case "COMBOBOX":
            case "LISTBOX":
                case "DROPDOWN":
                case "COMBOBOX":
                    let rawOptions: string[] = [];
                
                    try {
                        rawOptions = JSON.parse(fieldMeta?.listValues || "[]");
                    } catch {
                        console.warn("Failed to parse listValues as JSON array:", fieldMeta?.listValues);
                    }
                
                    const maxChoices = fieldMeta?.maxChoices ?? 1;
                    const selectedValues: string[] =
                        typeof value === "string" && maxChoices > 1
                            ? value.split(";").filter(Boolean)
                            : typeof value === "string" ? [value] : [];
                
                    return (
                        <FormControl fullWidth margin="dense">
                            <InputLabel>{label}</InputLabel>
                            <Select
                                multiple={maxChoices > 1}
                                value={selectedValues}
                                onChange={(e) => {
                                    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                                    const final = maxChoices > 1 ? selected.slice(0, maxChoices).join(";") : selected[0] || "";
                                    setValue(fieldId, final);
                                    triggerUserEvent(fieldId, "OnChange", { fieldId, value: final });
                                }}
                                renderValue={(selected: any) => maxChoices > 1 ? selected.join(", ") : selected[0]}
                            >
                                {rawOptions.map((opt: string) => (
                                    <MenuItem key={opt} value={opt}>
                                        {maxChoices > 1 && (
                                            <Checkbox checked={selectedValues.includes(opt)} />
                                        )}
                                        <Typography variant="body2">{opt}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                

        case "DATE":
            return (
                <TextField
                    {...commonProps}
                    type="date"
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
            );

        case "TIME":
            return (
                <TextField
                    {...commonProps}
                    type="time"
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
            );

        case "TIMESTAMP":
        case "DATETIME":
            return (
                <TextField
                    {...commonProps}
                    type="datetime-local"
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
            );

        case "FILE":
        case "BLOB":
            return (

                
                <FormControl fullWidth margin="dense">
                    <FormLabel>{label}</FormLabel>
                    <input
                        type="file"
                        disabled={readOnly}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                    setValue(fieldId, reader.result);
                                    triggerUserEvent(fieldId, "OnChange", {
                                        value: reader.result,
                                        fieldId,
                                    });
                                };
                                reader.readAsDataURL(file);
                            }
                        }}
                    />

                {viewerOpen && viewerData && (
                    <BlobViewerDialog
                    open={viewerOpen}
                    onClose={() => setViewerOpen(false)}
                    fieldLabel={viewerData.fieldLabel}
                    fileID={viewerData.fileID}
                    mimeType={viewerData.mimeType}
                    isBlob={viewerData.isBlob}
                    />
                )}

                {value && typeof value === "string" && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();

                        let fileID = value;
                        let mimeType: string | undefined = undefined;

                        if (value.includes(":")) {
                        const [extOrType, guid] = value.split(":");
                        fileID = guid;
                        mimeType = getMimeTypeFromExtension(extOrType);
                        }

                        setViewerData({
                        fieldLabel: label,
                        fileID,
                        mimeType,
                        isBlob: fieldMeta?.dataType === "Blob"
                        });
                        setViewerOpen(true);
                    }}
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    >
                    View File
                    </a>
                </Typography>
                )}


                </FormControl>
            );

        case "LABEL":
            return (
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {label}
                </Typography>
            );

        case "CUSTOM_HTML":
            return (
                <div
                    className="custom-html"
                    dangerouslySetInnerHTML={{
                        __html: (field as any).html || "<em>No custom HTML defined</em>",
                    }}
                />
            );

        default:
            return (
                <Tooltip title={`Unsupported control type: ${dataType}`} arrow>
                    <div
                        style={{
                            border: "1px dashed #999",
                            padding: "10px",
                            fontSize: "0.875rem",
                            backgroundColor: "#f2f2f2",
                            borderRadius: "6px",
                            color: "#666",
                        }}
                    >
                        <em>Unknown control type: {dataType}</em>
                    </div>
                </Tooltip>
            );
    }

    
};

export default ControlRenderer;
